import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, Loader2, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http, errorText, fmtPrice } from "@/lib/api";

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/login", { email, password });
      onDone();
    } catch (err) {
      toast.error(errorText(err));
    }
    setBusy(false);
  };
  const field = "mt-2 w-full border-b border-white/20 bg-transparent pb-2 text-lg outline-none focus:border-[var(--gold)]";
  return (
    <form onSubmit={submit} className="mx-auto mt-24 max-w-sm space-y-6" data-testid="admin-login-form">
      <img src="/media/logo-light.png" alt="Stillworking" className="h-10" />
      <p className="eyebrow">Admin area</p>
      <label className="block"><span className="text-xs text-[var(--stone)]">Email</span><input data-testid="admin-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={field} /></label>
      <label className="block"><span className="text-xs text-[var(--stone)]">Password</span><input data-testid="admin-login-passcode-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={field} /></label>
      <button data-testid="admin-login-submit-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] py-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink)]">
        {busy && <Loader2 size={14} className="animate-spin" />} Sign in
      </button>
    </form>
  );
}

function Dashboard({ onLogout }) {
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const load = () => {
    http.get("/admin/subscribers").then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    http.get("/admin/orders").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    const r = await http.get("/admin/subscribers.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stillworking-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const remove = async (id) => {
    await http.delete(`/admin/subscribers/${id}`);
    load();
  };
  const paid = orders.filter((o) => o.payment_status === "paid");
  const revenue = paid.reduce((s, o) => s + (o.amount || 0), 0);
  return (
    <div className="mx-auto max-w-6xl py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <img src="/media/logo-light.png" alt="Stillworking" className="h-8" />
        <button data-testid="admin-logout-button" onClick={onLogout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--stone)] hover:text-[var(--gold)]"><LogOut size={14} /> Sign out</button>
      </div>
      <div className="mt-12 grid gap-4 sm:grid-cols-3">
        {[["Subscribers", subs.length, "stat-subscribers"], ["Sales", paid.length, "stat-sales"], ["Revenue", fmtPrice(revenue), "stat-revenue"]].map(([l, v, id]) => (
          <div key={l} className="border border-white/10 bg-[var(--ink-2)] p-6" data-testid={id}>
            <p className="eyebrow">{l}</p><p className="mt-3 font-display text-5xl font-extrabold">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-14 flex items-center justify-between">
        <h2 className="font-display text-3xl font-extrabold uppercase">Mailing list</h2>
        <button data-testid="admin-export-csv-btn" onClick={exportCsv} className="flex items-center gap-2 rounded-full border border-[var(--gold)]/60 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ink)]"><Download size={13} /> Export CSV</button>
      </div>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="admin-subscribers-table">
          <thead className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--stone)]"><tr><th className="py-3">Email</th><th>Name</th><th>Date</th><th /></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} className="border-t border-white/10">
                <td className="py-3">{s.email}</td><td>{s.name || "—"}</td><td className="font-mono text-xs text-[var(--stone)]">{new Date(s.created_at).toLocaleString("it-IT")}</td>
                <td className="text-right"><button data-testid={`admin-delete-sub-${s.id}`} onClick={() => remove(s.id)} className="text-[var(--stone)] hover:text-red-400" aria-label="Elimina"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!subs.length && <tr><td colSpan={4} className="py-6 text-[var(--stone)]">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="mt-16 font-display text-3xl font-extrabold uppercase">Orders</h2>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="admin-orders-table">
          <thead className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--stone)]"><tr><th className="py-3">Track</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/10">
                <td className="py-3">{o.track_title}</td><td>{fmtPrice(o.amount)}</td>
                <td className={o.payment_status === "paid" ? "text-[var(--gold)]" : "text-[var(--stone)]"}>{o.payment_status}</td>
                <td className="font-mono text-xs text-[var(--stone)]">{new Date(o.created_at).toLocaleString("it-IT")}</td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan={4} className="py-6 text-[var(--stone)]">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setAuthed(false);
  };
  return (
    <div className="min-h-screen px-5 md:px-10">
      <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 pt-6 font-mono text-xs uppercase tracking-[0.2em] text-[var(--stone)] hover:text-[var(--gold)]"><ArrowLeft size={14} /> Site</Link>
      {authed === null && <Loader2 className="mx-auto mt-24 animate-spin text-[var(--gold)]" />}
      {authed === false && <Login onDone={() => setAuthed(true)} />}
      {authed === true && <Dashboard onLogout={logout} />}
    </div>
  );
}
